/**
 * Admin Users Management - Create and Edit Users
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Search, ChevronLeft, MoreVertical, Plus,
  Crown, User, Building2, Briefcase, Shield, Mail, Phone, Trash2, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// Role badge component
const RoleBadge = ({ role }) => {
  const config = {
    admin: { icon: Shield, color: "bg-purple-500/20 text-purple-400", label: "Admin" },
    employee: { icon: Briefcase, color: "bg-blue-500/20 text-blue-400", label: "Empleado" },
    club_owner: { icon: Building2, color: "bg-green-500/20 text-green-400", label: "Propietario" },
    player_premium: { icon: Crown, color: "bg-yellow-500/20 text-yellow-400", label: "Premium" },
    player_free: { icon: User, color: "bg-gray-500/20 text-gray-400", label: "Free" },
  };
  
  const { icon: Icon, color, label } = config[role] || config.player_free;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Plan badge component
const PlanBadge = ({ plan }) => {
  const config = {
    pro: { color: "bg-primary/20 text-primary", label: "Pro" },
    premium: { color: "bg-yellow-500/20 text-yellow-400", label: "Premium" },
    free: { color: "bg-gray-500/20 text-gray-400", label: "Free" },
  };
  
  const { color, label } = config[plan] || config.free;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
};

// User row component
const UserRow = ({ user, onEdit, onDelete, clubs }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-primary/30 transition-colors"
  >
    {/* Avatar */}
    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
      <span className="font-bold text-primary">
        {user.first_name?.[0]}{user.last_name?.[0]}
      </span>
    </div>
    
    {/* Info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-medium truncate">
          {user.first_name} {user.last_name}
        </p>
        <RoleBadge role={user.role} />
        {!user.is_active && (
          <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
            Inactivo
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground truncate">{user.email}</p>
      {user.role === 'club_owner' && user.club_id && (
        <p className="text-xs text-green-400 truncate">
          🏟️ {clubs?.find(c => c.id === user.club_id)?.name || 'Club asignado'}
        </p>
      )}
    </div>
    
    {/* Plan */}
    <div className="hidden sm:block">
      <PlanBadge plan={user.plan} />
    </div>
    
    {/* Actions */}
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={() => onEdit(user)} title="Editar usuario">
        <MoreVertical className="w-4 h-4" />
      </Button>
      <Button 
        variant="ghost" 
        size="icon" 
        onClick={() => onDelete(user)}
        className="text-destructive hover:text-destructive hover:bg-destructive/10"
        title="Eliminar usuario"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  </motion.div>
);

// Generate a random password
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < 10; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Create/Edit user dialog
const UserDialog = ({ user, open, onClose, onSave, isCreating, clubs }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'player_free',
    plan: 'free',
    is_active: true,
    password: '',
    club_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  
  useEffect(() => {
    if (user && !isCreating) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'player_free',
        plan: user.plan || 'free',
        is_active: user.is_active ?? true,
        password: '', // No password on edit
        club_id: user.club_id || '',
      });
    } else if (isCreating) {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'player_free',
        plan: 'free',
        is_active: true,
        password: generatePassword(), // Auto-generate password
        club_id: '',
      });
    }
    setErrors({});
    setCopiedPassword(false);
  }, [user, isCreating, open]);
  
  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleRegeneratePassword = () => {
    setFormData({ ...formData, password: generatePassword() });
    setCopiedPassword(false);
  };
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'Requerido';
    if (!formData.last_name.trim()) newErrors.last_name = 'Requerido';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Requerido';
    }
    if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      if (isCreating) {
        await onSave(null, formData);
      } else {
        await onSave(user.id, formData);
      }
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isCreating ? 'Crear nuevo usuario' : 'Editar usuario'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre</label>
              <Input
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="Nombre"
                className={errors.first_name ? 'border-red-500' : ''}
              />
              {errors.first_name && (
                <p className="text-xs text-red-400 mt-1">{errors.first_name}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Apellido</label>
              <Input
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Apellido"
                className={errors.last_name ? 'border-red-500' : ''}
              />
              {errors.last_name && (
                <p className="text-xs text-red-400 mt-1">{errors.last_name}</p>
              )}
            </div>
          </div>
          
          {/* Email */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Email (opcional)</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="usuario@ejemplo.com"
                className={`pl-9 ${errors.email ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-400 mt-1">{errors.email}</p>
            )}
          </div>
          
          {/* Phone */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Teléfono (ID del usuario)</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+52 1234567890"
                className={`pl-9 ${errors.phone ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.phone && (
              <p className="text-xs text-red-400 mt-1">{errors.phone}</p>
            )}
          </div>
          
          {/* Password field - only for new users */}
          {isCreating && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">Contraseña generada</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="font-mono pr-20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? 'Ocultar' : 'Ver'}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyPassword}
                  className="shrink-0"
                >
                  {copiedPassword ? '✓ Copiada' : 'Copiar'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRegeneratePassword}
                  className="shrink-0"
                >
                  ↻
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Comparte esta contraseña con el usuario. Podrá cambiarla después.
              </p>
            </div>
          )}
          
          {/* Role select */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Rol</label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary border border-border"
            >
              <option value="player_free">Jugador Free</option>
              <option value="player_premium">Jugador Premium</option>
              <option value="club_owner">Propietario de Club</option>
              <option value="employee">Empleado Hiksport</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          
          {/* Club select - only for club_owner */}
          {formData.role === 'club_owner' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">Club asignado</label>
              <select
                value={formData.club_id}
                onChange={(e) => handleChange('club_id', e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-secondary border border-border"
              >
                <option value="">-- Seleccionar club --</option>
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>
                    {club.name} ({club.city})
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                El propietario podrá gestionar este club
              </p>
            </div>
          )}
          
          {/* Plan select */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Plan</label>
            <select
              value={formData.plan}
              onChange={(e) => handleChange('plan', e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary border border-border"
            >
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          
          {/* Active toggle */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div>
              <p className="font-medium">Usuario activo</p>
              <p className="text-xs text-muted-foreground">Puede usar la aplicación</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('is_active', !formData.is_active)}
              className={`w-12 h-6 rounded-full transition-colors ${
                formData.is_active ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                formData.is_active ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
        </div>
        
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : isCreating ? 'Crear usuario' : 'Guardar cambios'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [total, setTotal] = useState(0);
  
  useEffect(() => {
    fetchUsers();
    fetchClubs();
  }, [roleFilter, planFilter]); // fetchUsers is defined below and depends on these filters
  
  const fetchClubs = async () => {
    try {
      const response = await adminAPI.getClubs();
      setClubs(response.data.clubs || []);
    } catch (err) {
      console.error('Clubs fetch error:', err);
    }
  };
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers({
        role: roleFilter || undefined,
        plan: planFilter || undefined,
        search: searchQuery || undefined,
      });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Users fetch error:', err);
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = () => {
    fetchUsers();
  };
  
  const handleSaveUser = async (userId, data) => {
    try {
      const normalizedPhone = `+${String(data.phone || '').replace(/\D/g, '')}`;
      const payload = {
        ...data,
        phone: normalizedPhone,
        email: data.email?.trim() || null,
      };

      if (userId) {
        // Update existing user
        await adminAPI.updateUser(userId, payload);
        toast.success('Usuario actualizado');
      } else {
        // Create new user
        await adminAPI.createUser(payload);
        toast.success('Usuario creado exitosamente');
      }
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || 'Error guardando usuario';
      toast.error(message);
      throw err;
    }
  };
  
  const handleOpenCreate = () => {
    setEditingUser(null);
    setIsCreating(true);
  };
  
  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setIsCreating(false);
  };
  
  const handleDeleteUser = async (user) => {
    if (!confirm(`¿Estás seguro de eliminar al usuario "${user.email}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await adminAPI.deleteUser(user.id);
      toast.success(response.data.message || 'Usuario eliminado');
      fetchUsers(); // Refresh list
    } catch (err) {
      console.error('Delete user error:', err);
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };
  
  const handleCloseDialog = () => {
    setEditingUser(null);
    setIsCreating(false);
  };
  
  // Filter users by search query locally for instant feedback
  const filteredUsers = users
    .filter(user => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        user.first_name?.toLowerCase().includes(query) ||
        user.last_name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    })
    // Sort alphabetically by full name
    .sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase().trim();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().trim();
      return nameA.localeCompare(nameB, 'es');
    });
  
  // Count test users (emails containing "test" or names containing "test")
  const testUsersCount = users.filter(u => 
    u.email?.toLowerCase().includes('test') || 
    u.first_name?.toLowerCase().includes('test') ||
    u.last_name?.toLowerCase().includes('test')
  ).length;
  
  // Delete test users function
  const handleDeleteTestUsers = async () => {
    if (!confirm(`¿Estás seguro de eliminar ${testUsersCount} usuarios de prueba? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await adminAPI.deleteTestUsers();
      toast.success(response.data.message || `${response.data.deleted_count || 0} usuarios de prueba eliminados`);
      fetchUsers();
    } catch (err) {
      console.error('Delete test users error:', err);
      toast.error(err.response?.data?.detail || 'Error eliminando usuarios de prueba');
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-4 p-4 max-w-6xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-lg">Gestión de usuarios</h1>
            <p className="text-xs text-muted-foreground">{total} usuarios registrados (ordenados A-Z)</p>
          </div>
          <div className="flex items-center gap-2">
            {testUsersCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDeleteTestUsers}
                className="gap-2 text-red-400 border-red-400/50 hover:bg-red-500/20"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Eliminar test ({testUsersCount})</span>
              </Button>
            )}
            <Button onClick={handleOpenCreate} className="gap-2">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Crear usuario</span>
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Search and filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-9"
            />
          </div>
          
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-10 px-3 rounded-lg bg-secondary border border-border text-sm"
            >
              <option value="">Todos los roles</option>
              <option value="admin">Admin</option>
              <option value="employee">Empleado</option>
              <option value="club_owner">Propietario</option>
              <option value="player_premium">Premium</option>
              <option value="player_free">Free</option>
            </select>
            
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="h-10 px-3 rounded-lg bg-secondary border border-border text-sm"
            >
              <option value="">Todos los planes</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
              <option value="free">Free</option>
            </select>
          </div>
        </div>
        
        {/* Users list */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Cargando usuarios...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No se encontraron usuarios</p>
              <Button onClick={handleOpenCreate} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Crear primer usuario
              </Button>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <UserRow 
                key={user.id} 
                user={user} 
                onEdit={handleOpenEdit}
                onDelete={handleDeleteUser}
                clubs={clubs}
              />
            ))
          )}
        </div>
      </div>
      
      {/* Create/Edit dialog */}
      <UserDialog
        user={editingUser}
        open={!!editingUser || isCreating}
        onClose={handleCloseDialog}
        onSave={handleSaveUser}
        isCreating={isCreating}
        clubs={clubs}
      />
    </div>
  );
}
